/**
 * FastPerformancePanel — dashboard rendering of the Fast by NoFee
 * program. Shows the restaurant's Fast Score, verified on-time
 * delivery counters, the program explainer, and recent customer
 * verifications.
 *
 * iter134 (Jul 2026) — Owner spec: retire the public tier ladder
 * (Standard / Plus / Premium / Thunder). Fast Score remains an
 * internal operational metric visible only inside this dashboard.
 * The tier hero + progress-to-next-tier are gone; Fast Score is
 * now shown as a single premium metric card.
 *
 * Endpoint: GET /api/fast/restaurants/{id}/panel (owner-authenticated)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Zap, CheckCircle2, AlertCircle, MessageSquare, TrendingUp } from 'lucide-react';
import FastProgramExplainer from './FastProgramExplainer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FastPerformancePanel = ({ restaurantId, token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!restaurantId || !token) return;
    axios
      .get(`${API}/fast/restaurants/${restaurantId}/panel`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load Fast performance.'));
  }, [restaurantId, token]);

  if (error) {
    return (
      <div className="space-y-3" data-testid="fast-panel-error">
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.05] text-rose-300 text-sm px-4 py-3">
          {error}
        </div>
        <EmptyFastPanel />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 animate-pulse" data-testid="fast-panel-loading">
        <div className="h-5 w-44 bg-white/[0.06] rounded mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 bg-white/[0.04] rounded-xl" />)}
        </div>
      </div>
    );
  }

  const points = Number(data.points || data.fast_score || 0);

  return (
    <div className="space-y-6" data-testid="fast-panel">
      {/* Fast Score hero — internal operational metric */}
      <div
        className="rounded-2xl border bg-gradient-to-r from-zinc-900/60 to-zinc-950 border-yellow-400/35 p-6"
        data-testid="fast-panel-score-hero"
      >
        <div className="flex items-center gap-1.5 mb-2">
          <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300" />
          <span className="text-[11px] font-black uppercase tracking-[0.18em] text-yellow-200">
            Fast by NoFee · Verified On-Time Delivery
          </span>
        </div>
        <div className="flex items-baseline gap-3 mt-2" data-testid="fast-panel-points">
          <span className="text-5xl font-black tabular-nums text-white">{points}</span>
          <span className="text-sm text-zinc-300">Fast Score</span>
        </div>
        <p className="mt-3 text-[12.5px] text-zinc-400 leading-relaxed max-w-2xl">
          Internal operational metric. Customers only see that your restaurant is Fast by NoFee — not the underlying score. Keep delivering within your promised delivery time to grow your score.
        </p>
      </div>

      {/* Compact secondary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fast-panel-secondary-stats">
        <Stat
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Verified On-Time"
          value={data.verified_deliveries}
          tone="good"
          testid="fast-kpi-verified"
        />
        <Stat
          icon={<AlertCircle className="w-4 h-4" />}
          label="Late Deliveries"
          value={data.late_deliveries}
          tone={data.late_deliveries > 0 ? 'bad' : 'neutral'}
          testid="fast-kpi-late"
        />
        <Stat
          icon={<TrendingUp className="w-4 h-4" />}
          label="On-Time Rate"
          value={`${data.on_time_rate}%`}
          tone={data.on_time_rate >= 80 ? 'good' : data.on_time_rate >= 50 ? 'neutral' : 'bad'}
          testid="fast-kpi-ontime"
        />
        <Stat
          icon={<MessageSquare className="w-4 h-4" />}
          label="Customer Verifications"
          value={data.rating_count || (data.verified_deliveries + data.late_deliveries)}
          tone="neutral"
          testid="fast-kpi-rating-count"
        />
      </div>

      {/* Program explainer */}
      <FastProgramExplainer testid="fast-panel-explainer" />

      {/* Recent reviews */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6" data-testid="fast-recent-reviews">
        <div className="flex items-center gap-2 text-white text-sm font-bold mb-3">
          <MessageSquare className="w-4 h-4 text-yellow-400" />
          Recent Customer Verifications
        </div>
        {!data.recent_reviews?.length ? (
          <p className="text-[12.5px] text-zinc-500" data-testid="fast-recent-empty">
            No customer verifications yet. As soon as your first orders are delivered, customers will be asked to confirm whether your promised time was met.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {data.recent_reviews.map((rv, idx) => (
              <li
                key={`${rv.created_at}-${idx}`}
                data-testid={`fast-review-${idx}`}
                className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
              >
                <div className="flex items-center gap-2 text-[12px]">
                  <span className={`inline-flex items-center gap-1 font-bold ${rv.on_time ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {rv.on_time ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {rv.on_time ? 'On time' : 'Late'}
                  </span>
                  <span className="ml-auto text-[10.5px] text-zinc-500">
                    {rv.created_at?.slice(0, 10)}
                  </span>
                </div>
                {rv.comment && (
                  <p className="text-[12.5px] text-zinc-300 mt-1 leading-snug">&ldquo;{rv.comment}&rdquo;</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value, tone, testid }) => {
  const toneCls = tone === 'good'
    ? 'border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-200'
    : tone === 'bad'
      ? 'border-rose-500/30 bg-rose-500/[0.05] text-rose-200'
      : 'border-white/[0.08] bg-white/[0.02] text-zinc-200';
  return (
    <div className={`rounded-xl border px-3 py-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider opacity-70">
        {icon} {label}
      </div>
      <div className="mt-1.5 text-2xl font-bold tabular-nums leading-none">{value}</div>
    </div>
  );
};

// Rendered when the API call fails so the operator still sees the
// program shell (Fast Score = 0) and the explainer block. Keeps the
// dashboard usable even if the /api/fast/restaurants/{id}/panel
// endpoint is briefly unavailable.
const EmptyFastPanel = () => (
  <div className="space-y-6" data-testid="fast-panel">
    <div
      className="rounded-2xl border bg-gradient-to-r from-zinc-900/60 to-zinc-950 border-yellow-400/35 p-6"
      data-testid="fast-panel-score-hero"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300" />
        <span className="text-[11px] font-black uppercase tracking-[0.18em] text-yellow-200">
          Fast by NoFee · Verified On-Time Delivery
        </span>
      </div>
      <div className="flex items-baseline gap-3 mt-2" data-testid="fast-panel-points">
        <span className="text-5xl font-black tabular-nums text-white">0</span>
        <span className="text-sm text-zinc-300">Fast Score</span>
      </div>
    </div>
    <FastProgramExplainer testid="fast-panel-explainer" />
  </div>
);

export default FastPerformancePanel;
